<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { debounce } from 'lodash';
  import { Building2, Search, X } from 'lucide-svelte';

  import { suggestCompanies, minQueryLength } from '$lib/api/autocomplete';
  import { addSample, maxSamples, neighborSamples, removeSample } from '$lib/stores/neighbors';
  import type { Suggestion } from '$lib/types/api';

  /**
   * The companies to find more like.
   *
   * # Not the sidebar's selector
   *
   * That one is built for a 320px column: an "Add Company" button that swaps
   * itself for an input, a 36px-tall suggestion list, `text-xs` throughout, and
   * three `style="background: none !important"` overrides fighting a global
   * rule. Here the search is simply always open, because asking for examples is
   * the entire step — there is nothing for the input to be hiding behind.
   *
   * What is shared is what should be: the lookup (`suggestCompanies`) and the
   * list itself (`neighborSamples`, `addSample`, `removeSample`), so the two
   * screens cannot disagree about the cap or about which endpoint answers.
   */

  let query = $state('');
  let suggestions = $state<Suggestion[]>([]);
  let loading = $state(false);

  const full = $derived($neighborSamples.length >= maxSamples);

  const lookup = debounce(async (text: string) => {
    // The reply that comes back is only interesting if the box still says what
    // it said when the request went out; typing on past a slow response should
    // not repopulate the list underneath the newer query.
    const asked = text;
    loading = true;
    suggestions = await suggestCompanies(text);
    if (asked !== query) return;
    loading = false;
  }, 200);

  function onInput(event: Event) {
    query = (event.currentTarget as HTMLInputElement).value;
    if (query.trim().length < minQueryLength) {
      suggestions = [];
      loading = false;
      lookup.cancel();
      return;
    }
    lookup(query);
  }

  function pick(suggestion: Suggestion) {
    if (!suggestion.available) return;
    if (!addSample(suggestion)) return;

    query = '';
    suggestions = [];
  }
</script>

<!--
  # Why this is laid out search-first, with the slots always drawn

  Everything except the search used to sit behind `{#if samples.length > 0}`,
  and nothing has been chosen when a customer arrives — so the state they
  always meet first was the one with the least in it: a `max-w-md` input adrift
  in a 22rem card, with a grey line of hint at the bottom and 300px of white
  in between. The step looked unfinished at exactly the moment it had to
  explain itself.

  So the slots are drawn whether or not they are filled. Five outlines say what
  the cap is and what the task looks like without a sentence having to; the
  hint keeps the nuance the outlines cannot carry, which is that one example is
  already enough.

  The search leads, because when the step opens it is the only thing there is
  to do. The chosen list used to lead on the argument that it is the answer and
  the search is merely the tool — true once something has been chosen, and the
  wrong way round for the arrival.
-->
<div class="space-y-5">
  {#if !full}
    <div class="max-w-xl">
      <div class="relative">
        <Search
          class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          value={query}
          oninput={onInput}
          placeholder={$_('form.placeholders.searchCompany')}
          class="w-full rounded-control border border-hairline bg-white py-2.5 pl-9 pr-8 text-sm placeholder-gray-400 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
        />
        {#if loading}
          <span
            class="absolute right-3 top-1/2 h-3 w-3 -translate-y-1/2 animate-spin rounded-full border-2 border-hairline border-t-accent-500"
          ></span>
        {/if}
      </div>

      {#if suggestions.length > 0}
        <ul
          class="mt-2 divide-y divide-hairline overflow-hidden rounded-surface border border-hairline bg-white shadow-raised"
        >
          {#each suggestions.slice(0, 8) as suggestion (suggestion.id)}
            <li>
              <button
                type="button"
                onclick={() => pick(suggestion)}
                disabled={!suggestion.available}
                class="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span class="min-w-0 flex-1 truncate text-gray-900">{suggestion.name}</span>
                {#if !suggestion.available}
                  <span class="shrink-0 text-2xs text-gray-400">{$_('guided.samples.taken')}</span>
                {/if}
              </button>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  {/if}

  <p class="max-w-xl text-xs text-gray-500">
    {full
      ? $_('guided.samples.full', { values: { max: maxSamples } })
      : $_('guided.samples.hint', { values: { max: maxSamples } })}
  </p>

  <div>
    <div class="mb-2 flex items-baseline justify-between gap-3">
      <span class="eyebrow">{$_('guided.samples.chosen')}</span>
      <span class="text-xs tabular-nums text-ink-500">
        {$neighborSamples.length} / {maxSamples}
      </span>
    </div>

    <ul class="grid gap-2 sm:grid-cols-2">
      {#each $neighborSamples as company (company.id)}
        <li
          class="flex items-center gap-3 rounded-surface border border-hairline bg-surface-sunken px-3 py-2"
        >
          <Building2 class="h-4 w-4 shrink-0 text-accent-500" />
          <span class="min-w-0 flex-1 truncate text-sm text-gray-900">{company.name}</span>
          <button
            type="button"
            onclick={() => removeSample(company.id)}
            class="shrink-0 rounded-control p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
            aria-label={$_('buttons.remove')}
          >
            <X class="h-4 w-4" />
          </button>
        </li>
      {/each}

      <!--
        The slots still to fill. Decorative and announced as such: a screen
        reader already has the count above, and reading out five empty list
        items would be noise rather than information.
      -->
      {#each Array(maxSamples - $neighborSamples.length) as _, slot (slot)}
        <li
          aria-hidden="true"
          class="flex items-center rounded-surface border border-dashed border-hairline-strong px-3 py-2"
        >
          <span class="h-4"></span>
        </li>
      {/each}
    </ul>
  </div>
</div>
